/**
 * @file VoiceConsentSheet.tsx
 * @brief "May we keep your voice?" — asked of the OTHER person, in their
 *        language, before an interpretation session starts. Web + desktop.
 *
 * @details
 * The web port of the phone's `S2SInterpreter/components/VoiceConsentSheet`
 * (DES 04-10-2026), same copy from the same endpoint:
 * - the question and text in Person B's language, a play button that speaks
 *   it, and a line telling them to tap green to agree, red if not;
 * - GREEN "keep my voice (Yes)", RED "don't keep my voice (No)", the same
 *   size so neither answer is pushed (GDPR Art. 7);
 * - the English below, for the owner.
 * Either answer starts the session; closing the sheet or Cancel does not.
 *
 * On "no" the server never stores Person B's ORIGINAL audio. The session runs
 * the same; transcript, translation and translated (AI voice) audio are kept.
 *
 * The prompt is fetched as soon as Person B's language is set (first time
 * per language ~5-8s, then cached on the server), so it is usually ready by
 * the time Start is pressed.
 *
 * Related: ../engine/s2sApi.ts (fetchVoiceConsentPrompt, personBVoiceConsent),
 *          BACKEND/src/features/s2s/voiceConsent.mjs
 *
 * @author DES
 * @date 04-10-2026
 */

import { useQuery } from "@tanstack/react-query";
import { Loader2, Play, Square } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/Sheet";
import { isRTLText } from "@/shared/utils/formatters";
import { cn } from "@/shared/theme/cn";
import { fetchVoiceConsentPrompt } from "../engine/s2sApi";

/** Arabic-script copy (Urdu Nastaliq especially) needs tall leading. */
function copyClass(text: string): string {
  return isRTLText(text) ? "leading-[2.1]" : "leading-[1.45]";
}

function useVoiceConsentPrompt(
  lang: string | null,
  locale: string | undefined,
  gender: string | null,
) {
  return useQuery({
    queryKey: ["s2s", "voice-consent-prompt", lang, locale ?? "", gender ?? ""],
    queryFn: () => fetchVoiceConsentPrompt({ lang: lang!, locale, gender }),
    enabled: !!lang,
    staleTime: Infinity,
    gcTime: 60 * 60 * 1000,
    retry: 1,
  });
}

export function VoiceConsentSheet({
  open,
  lang,
  locale,
  gender,
  onAnswer,
  onClose,
}: {
  open: boolean;
  /** Person B's language (e.g. "ur-PK" or "ur"). */
  lang: string | null;
  locale?: string;
  gender: string | null;
  onAnswer: (keepVoice: boolean) => void;
  onClose: () => void;
}) {
  const query = useVoiceConsentPrompt(lang, locale, gender);
  const prompt = query.data;
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const stop = () => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.currentTime = 0;
    }
    setPlaying(false);
  };

  // A new language's clip replaces the old one; closing stops it.
  useEffect(() => {
    stop();
    audioRef.current = null;
  }, [prompt]);
  useEffect(() => {
    if (!open) stop();
  }, [open]);

  const togglePlay = () => {
    if (playing) {
      stop();
      return;
    }
    if (!prompt?.audio_b64) return;
    if (!audioRef.current) {
      const a = new Audio(`data:${prompt.audio_mime || "audio/mpeg"};base64,${prompt.audio_b64}`);
      a.onended = () => setPlaying(false);
      a.onerror = () => setPlaying(false);
      audioRef.current = a;
    }
    setPlaying(true);
    audioRef.current.play().catch(() => setPlaying(false));
  };

  const answer = (keep: boolean) => {
    stop();
    onAnswer(keep);
  };

  const text = prompt?.text;
  const english = prompt?.english;
  const showEnglish = !!prompt && prompt.lang !== "en" && prompt.translated;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="sm:max-w-lg">
        <div className="px-6 pb-2 pr-12 pt-6">
          <SheetTitle className="text-base">Voice consent</SheetTitle>
          <SheetDescription className="sr-only">
            Ask the other person whether their original voice may be kept.
          </SheetDescription>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {query.isLoading || (query.isFetching && !prompt) ? (
            <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              Preparing it in their language…
            </div>
          ) : query.isError || !text ? (
            <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
              Couldn't load the consent text. Check the connection and try again.
              <Button variant="secondary" onClick={() => void query.refetch()}>
                Try again
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <h2
                dir={isRTLText(text.title) ? "rtl" : "ltr"}
                className={cn("text-[24px] font-semibold text-app-text", copyClass(text.title))}
              >
                {text.title}
              </h2>
              <p
                dir={isRTLText(text.body) ? "rtl" : "ltr"}
                className={cn("text-[18px] text-app-text", copyClass(text.body))}
              >
                {text.body}
              </p>

              {prompt?.audio_b64 ? (
                <button
                  type="button"
                  onClick={togglePlay}
                  aria-label={playing ? "Stop" : "Play the consent aloud"}
                  className="flex h-12 w-full items-center justify-center gap-2.5 rounded-full bg-secondary text-[16px] font-semibold text-app-text transition-colors hover:bg-secondary/80"
                >
                  {playing ? (
                    <Square className="h-4 w-4 fill-brand text-brand" />
                  ) : (
                    <Play className="h-5 w-5 fill-brand text-brand" />
                  )}
                  {playing ? "Stop" : `${text.listen || "Listen"}${showEnglish ? "  (Play)" : ""}`}
                  {playing ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : null}
                </button>
              ) : null}

              {text.instruction ? (
                <p
                  dir={isRTLText(text.instruction) ? "rtl" : "ltr"}
                  className={cn(
                    "text-[16px] font-semibold text-app-text",
                    copyClass(text.instruction),
                  )}
                >
                  {text.instruction}
                </p>
              ) : null}

              {showEnglish && english ? (
                <p className="text-[14px] leading-[1.45] text-muted-foreground">
                  {english.body} {english.instruction}
                </p>
              ) : null}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2.5 px-6 pb-6 pt-3">
          <button
            type="button"
            disabled={!text}
            onClick={() => answer(true)}
            dir={text && isRTLText(text.keep) ? "rtl" : "ltr"}
            className="h-12 w-full rounded-full bg-emerald-500 px-5 text-[16px] font-semibold text-white transition-colors hover:bg-emerald-600 disabled:opacity-50"
          >
            {text?.keep || "Yes, keep my voice"}
            {showEnglish ? "  (Yes)" : ""}
          </button>
          <button
            type="button"
            disabled={!text}
            onClick={() => answer(false)}
            dir={text && isRTLText(text.decline) ? "rtl" : "ltr"}
            className="h-12 w-full rounded-full bg-brand px-5 text-[16px] font-semibold text-white transition-colors hover:bg-brand/90 disabled:opacity-50"
          >
            {text?.decline || "No, don't keep my voice"}
            {showEnglish ? "  (No)" : ""}
          </button>
          {/* Both answers start the session, so a wrong start needs a way out
              here (DES 06-10-2026). Owner's chrome: English, a grey link, live
              while the prompt loads. Nothing is created or billed. */}
          <button
            type="button"
            onClick={onClose}
            className="h-12 w-full rounded-full px-5 text-[16px] font-medium text-muted-foreground transition-colors hover:text-app-text"
          >
            Cancel
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
